/**
 * RichTextEditor — contentEditable compose area with formatting toolbar.
 * No third-party deps. Uses document.execCommand (broadly supported, works for legal email).
 * Outputs HTML via onHtmlChange for sending; also exposes plain text for fallback.
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import {
  TextB,
  TextItalic,
  TextUnderline,
  ListBullets,
  ListNumbers,
  Link,
  TextStrikethrough,
  Quotes,
} from '@phosphor-icons/react';

interface Props {
  placeholder?: string;
  defaultValue?: string;
  onHtmlChange: (html: string) => void;
  minHeight?: number;
  autoFocus?: boolean;
}

interface ToolbarButtonProps {
  title: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  active?: boolean;
}

function ToolbarButton({ title, onClick, children, active }: ToolbarButtonProps) {
  return (
    <button
      title={title}
      onMouseDown={(e) => {
        e.preventDefault(); // Prevent focus loss from editor
        onClick(e);
      }}
      style={{
        width: 28, height: 28,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', borderRadius: 4,
        background: active ? '#e5e7eb' : 'transparent',
        color: active ? '#111827' : '#6b7280',
        cursor: 'pointer', flexShrink: 0,
        transition: 'background 0.1s',
      }}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return (
    <div style={{
      width: 1, height: 18, background: 'var(--border-default)',
      margin: '0 2px', flexShrink: 0,
    }} />
  );
}

type FormatState = {
  bold: boolean; italic: boolean; underline: boolean; strikeThrough: boolean;
  insertUnorderedList: boolean; insertOrderedList: boolean;
};

const EMPTY_FORMATS: FormatState = {
  bold: false, italic: false, underline: false, strikeThrough: false,
  insertUnorderedList: false, insertOrderedList: false,
};

export function RichTextEditor({ placeholder, defaultValue, onHtmlChange, minHeight = 200, autoFocus }: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<FormatState>(EMPTY_FORMATS);

  // Reflect the caret's current formatting onto the toolbar buttons.
  const refreshActive = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !el.contains(sel.anchorNode)) return;
    try {
      setActive({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        strikeThrough: document.queryCommandState('strikeThrough'),
        insertUnorderedList: document.queryCommandState('insertUnorderedList'),
        insertOrderedList: document.queryCommandState('insertOrderedList'),
      });
    } catch { /* queryCommandState unsupported — ignore */ }
  }, []);

  useEffect(() => {
    document.addEventListener('selectionchange', refreshActive);
    return () => document.removeEventListener('selectionchange', refreshActive);
  }, [refreshActive]);

  useEffect(() => {
    if (editorRef.current && defaultValue && editorRef.current.innerHTML !== defaultValue) {
      editorRef.current.innerHTML = defaultValue;
    }
  }, []);  // Only on mount

  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
      // Move cursor to end
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [autoFocus]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onHtmlChange(editorRef.current.innerHTML);
    }
  }, [onHtmlChange]);

  function exec(command: string, value?: string) {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
    refreshActive();
  }

  function insertLink() {
    const url = prompt('Enter URL:', 'https://');
    if (url) exec('createLink', url);
  }

  const toolbarStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 1,
    padding: '6px 10px',
    borderBottom: '1px solid var(--border-default)',
    background: 'var(--surface)',
    flexWrap: 'wrap',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Formatting toolbar */}
      <div style={toolbarStyle}>
        <ToolbarButton title="Bold (Ctrl+B)" active={active.bold} onClick={() => exec('bold')}>
          <TextB size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton title="Italic (Ctrl+I)" active={active.italic} onClick={() => exec('italic')}>
          <TextItalic size={14} />
        </ToolbarButton>
        <ToolbarButton title="Underline (Ctrl+U)" active={active.underline} onClick={() => exec('underline')}>
          <TextUnderline size={14} />
        </ToolbarButton>
        <ToolbarButton title="Strikethrough" active={active.strikeThrough} onClick={() => exec('strikeThrough')}>
          <TextStrikethrough size={14} />
        </ToolbarButton>

        <ToolbarSep />

        <ToolbarButton title="Bullet list" active={active.insertUnorderedList} onClick={() => exec('insertUnorderedList')}>
          <ListBullets size={14} />
        </ToolbarButton>
        <ToolbarButton title="Numbered list" active={active.insertOrderedList} onClick={() => exec('insertOrderedList')}>
          <ListNumbers size={14} />
        </ToolbarButton>
        <ToolbarButton title="Block quote" onClick={() => exec('formatBlock', 'blockquote')}>
          <Quotes size={14} />
        </ToolbarButton>

        <ToolbarSep />

        <ToolbarButton title="Insert link" onClick={insertLink}>
          <Link size={14} />
        </ToolbarButton>

        <ToolbarSep />

        {/* Font family */}
        <select
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => {
            if (e.target.value) exec('fontName', e.target.value);
            e.target.value = '';
          }}
          defaultValue=""
          style={{
            border: 'none', background: 'transparent',
            fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer',
            outline: 'none', padding: '2px 4px', maxWidth: 110,
          }}
          title="Font"
        >
          <option value="" disabled>Font</option>
          <option value="'IBM Plex Sans', sans-serif">Sans Serif</option>
          <option value="Georgia, 'Times New Roman', serif">Serif</option>
          <option value="Arial, Helvetica, sans-serif">Arial</option>
          <option value="'Times New Roman', serif">Times</option>
          <option value="'Courier New', monospace">Monospace</option>
        </select>

        {/* Font size */}
        <select
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => {
            exec('fontSize', e.target.value);
            e.target.value = '3'; // reset
          }}
          defaultValue="3"
          style={{
            border: 'none', background: 'transparent',
            fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer',
            outline: 'none', padding: '2px 0',
          }}
          title="Font size"
        >
          <option value="1">Small</option>
          <option value="3">Normal</option>
          <option value="4">Large</option>
          <option value="5">X-Large</option>
        </select>
      </div>

      {/* Editor area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={(e) => {
          // Keyboard shortcuts
          if (e.ctrlKey || e.metaKey) {
            if (e.key === 'b') { e.preventDefault(); exec('bold'); }
            if (e.key === 'i') { e.preventDefault(); exec('italic'); }
            if (e.key === 'u') { e.preventDefault(); exec('underline'); }
          }
        }}
        data-placeholder={placeholder}
        style={{
          flex: 1,
          minHeight,
          padding: '12px 16px',
          fontFamily: "'IBM Plex Sans', sans-serif",
          fontSize: 14,
          color: 'var(--text-primary)',
          lineHeight: 1.7,
          outline: 'none',
          overflowY: 'auto',
        }}
      />

      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] ul { padding-left: 20px; margin: 4px 0; }
        [contenteditable] ol { padding-left: 20px; margin: 4px 0; }
        [contenteditable] blockquote {
          border-left: 3px solid #d1d5db;
          margin: 6px 0 6px 4px;
          padding-left: 12px;
          color: #6b7280;
        }
        [contenteditable] a { color: #2563eb; text-decoration: underline; }
      `}</style>
    </div>
  );
}
